/**
 * Pure DOM helper — pick the `<video>` element AnnotationOverlay should
 * mount over.
 *
 * Preference order (resolved per call):
 *   1. Whichever `<video>` lives inside `[data-cvp-role="dominant-tile"]`.
 *      RoomLayout places this marker on whichever tile its
 *      `pickDominantTrack` helper resolved — screen-share, an
 *      operator-promoted camera, the field's camera, or the local
 *      fallback. This is what makes "draw on screen-share" work: when
 *      the screen-share track wins dominance, the marker moves with it
 *      and the overlay re-anchors automatically (the polling sweep in
 *      AnnotationOverlay's useEffect catches the swap within ~1s).
 *   2. Legacy LiveKit selector — used as a fallback for tests that mount
 *      AnnotationOverlay outside of RoomLayout (so no dominant-tile
 *      attribute is present). Operator (editable=true) sees the field's
 *      stream as REMOTE; field (editable=false) sees it as LOCAL.
 *
 * Extracted from AnnotationOverlay so the selector preference has its
 * own regression coverage. The component's useEffect uses this verbatim.
 */
export function findTargetVideo(opts: {
  doc: Pick<Document, "querySelector">;
  editable: boolean;
}): HTMLVideoElement | null {
  const { doc, editable } = opts;
  const dominantTile = doc.querySelector('[data-cvp-role="dominant-tile"]');
  if (dominantTile) {
    const inDominant = dominantTile.querySelector("video") as
      | HTMLVideoElement
      | null;
    if (inDominant) return inDominant;
  }
  const localSelector =
    'video[data-lk-source="camera"][data-lk-local-participant="true"]';
  const remoteSelector =
    'video[data-lk-source="camera"]:not([data-lk-local-participant="true"])';
  const primary = editable ? remoteSelector : localSelector;
  const fallback = editable ? localSelector : remoteSelector;
  return (
    (doc.querySelector(primary) as HTMLVideoElement | null) ??
    (doc.querySelector(fallback) as HTMLVideoElement | null)
  );
}
